import type { FormModel } from './form';
import { uuid } from 'vue-uuid';

export interface NetworkGroup {
    identifier: string
    investigationIdentifier: string
    name: string
    permissions: NetworkGroupPermissions
    activeUsers: NetworkGroupUser[]
    pendingUsers: NetworkGroupUser[]
    formsPermissions: boolean | string[]
    editFormsPermissions: boolean | string[]
    invitationLink: NetworkGroupInvitationLink
    creatorIdentifier: string
    editorIdentifier: string
    createdAt: string
    updatedAt: string
}

export interface NetworkGroupPermissions {
    viewPermissions: string
    exportPermissions: boolean
    manageStudyPermissions: boolean
    editCases?: boolean
}

interface NetworkGroupUser {
    identifier: string
    avatar?: string
    names?: string
    email: string
}

interface NetworkGroupInvitationLink {
    identifier: string
    link: string
}

interface IGroupInviteLink {
    identifier: string
    link: string
}

export interface IStudyGroupPermissions {
    viewEntries: boolean;
    addEntries?: boolean;
    export: boolean;
    manageInvest: boolean;
    editCases?: boolean;
    createStudy?: boolean;
    forms: boolean | string[];
    editForms: boolean | string[];
}

export interface IStudyGroupUser {
    identifier: string
    avatar?: string
    name?: string
    email: string
    status: string
}

export interface IStudyGroup {
    identifier: string
    studyIdentifier: string
    name: string
    permissions: IStudyGroupPermissions
    activeUsers: IStudyGroupUser[]
    pendingUsers: IStudyGroupUser[]
    members: string;
    inviteLink: IGroupInviteLink | undefined
    creatorIdentifier: string
    editorIdentifier: string
    createdAt: Date
    updatedAt: Date
}

export class StudyGroupModel {
  identifier: string;
  studyIdentifier: string;
  name: string;
  permissions: IStudyGroupPermissions;
  activeUsers: IStudyGroupUser[];
  pendingUsers: IStudyGroupUser[];
  members: string;
  inviteLink: IGroupInviteLink | undefined;
  creatorIdentifier: string;
  editorIdentifier: string;
  createdAt: Date;
  updatedAt: Date;

  create?: boolean;

  constructor({
    identifier,
    studyIdentifier,
    name,
    permissions,
    activeUsers,
    pendingUsers,
    members,
    inviteLink,
    creatorIdentifier,
    editorIdentifier,
    createdAt,
    updatedAt
  }: IStudyGroup) {
    this.identifier = identifier;
    this.studyIdentifier = studyIdentifier;
    this.name = name;
    this.permissions = permissions;
    this.activeUsers = activeUsers;
    this.pendingUsers = pendingUsers;
    this.members = members;
    this.inviteLink = inviteLink;
    this.creatorIdentifier = creatorIdentifier;
    this.editorIdentifier = editorIdentifier;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
  }

  static mapFromBackend(networkStudyGroup: NetworkGroup) {
    const networkGroupPermissions = networkStudyGroup.permissions;
    const groupPermissions: IStudyGroupPermissions = {
      viewEntries: networkGroupPermissions.viewPermissions == 'groupEntries',
      export: networkGroupPermissions.exportPermissions,
      manageInvest: networkGroupPermissions.manageStudyPermissions,
      forms: networkStudyGroup.formsPermissions,
      editForms: networkStudyGroup.editFormsPermissions
    };
    const inviteLink: IGroupInviteLink | undefined = networkStudyGroup.invitationLink ? {
      identifier: networkStudyGroup.invitationLink.identifier,
      link: networkStudyGroup.invitationLink.link
    } : undefined;

    const membersCount = networkStudyGroup.activeUsers.length + networkStudyGroup.pendingUsers.length;
    const members = membersCount == 0 ? 'No members' : membersCount == 1 ? `${membersCount} member` : `${membersCount} members`;

    return new StudyGroupModel({
      identifier: networkStudyGroup.identifier,
      studyIdentifier: networkStudyGroup.investigationIdentifier,
      name: networkStudyGroup.name,
      permissions: groupPermissions,
      activeUsers: networkStudyGroup.activeUsers.map(user => this.mapUser(user)),
      pendingUsers: networkStudyGroup.pendingUsers.map(user => this.mapUser(user)),
      members: members,
      inviteLink: inviteLink,
      creatorIdentifier: networkStudyGroup.creatorIdentifier,
      editorIdentifier: networkStudyGroup.editorIdentifier,
      createdAt: new Date(networkStudyGroup.createdAt),
      updatedAt: new Date(networkStudyGroup.updatedAt)
    });
  }

  static mapUser(networkGroupUser: NetworkGroupUser): IStudyGroupUser {
    return {
      identifier: networkGroupUser.identifier,
      name: networkGroupUser.names,
      email: networkGroupUser.email,
      avatar: networkGroupUser.avatar,
      status: networkGroupUser.names ? 'Activated' : 'Invited'
    };
  }

  static createEmpty(formList: FormModel[]) {
    const permissions: IStudyGroupPermissions = {
      viewEntries: true,
      export: false,
      manageInvest: false,
      forms: formList.map(form => form.base64Id) as string[],
      editForms: formList.map(form => form.base64Id) as string[]
    };
    const studyGroup: IStudyGroup = {
      identifier: uuid.v4(),
      studyIdentifier: '',
      name: 'Unnamed group',
      permissions,
      activeUsers: [],
      pendingUsers: [],
      members: '',
      inviteLink: undefined,
      creatorIdentifier: '',
      editorIdentifier: '',
      createdAt: new Date(),
      updatedAt: new Date()
    };
    return new StudyGroupModel(studyGroup);
  }
}
